import React, { useEffect, useState } from "react"
import axios from "axios"
import { useForm } from "react-hook-form"
import Header from "../../../components/navigation/Header"
import Footer from "../../../components/navigation/Footer"
import { useSearchParams } from "react-router-dom"
import LoadingAnimation from "../../../components/general/LoadingAnimation"
import CreateCSFR from "../../../hooks/userAuthentication/CreateCSFR"

interface FormStructure {
    id: string;
    summary: string;
    firstLink: string;
    secondLink: string;
    thirdLink: string;
    certified: boolean;
    certificationFile: FileList;
    recordCheck: boolean;
    disclaimer: boolean;
}

interface CoachProfile {
    isActive: boolean;
    exists: boolean;
}

export default function ConfirmEmail(){
    const csrfToken = CreateCSFR({ name: "csrftoken" })
    const [queryParameters] = useSearchParams()
    const idFromURL = queryParameters.get("id")
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [coachExists, setCoachExists] = useState<CoachProfile>()

    useEffect(()=>{
        document.title = "Coach Screening"
    },[])

    // React Hook Form setup
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormStructure>()

    // Function to check if coach has already submitted form
    useEffect(()=>{
        if(idFromURL){
            axios.get(`${process.env.REACT_APP_SKILLJA_URL}/screening/get_coach_screening_status/?id=${idFromURL}`, {
                headers: {
                    "X-CSRFToken": csrfToken,
                    "Content-Type": "application/json",
                },
                withCredentials: true,
            })
            .then((res) => {
                if (res.status === 200) {
                    const data = res.data
                    setCoachExists(data)
                    // Prevent coach from resubmitting if account is already active
                    if (data.isActive) {
                        setHasSubmitted(true)
                    }
                } 
                // No coach found with given URL email, set with dummy data to prevent submission
                else if (res.status === 204){
                    setCoachExists({isActive: true, exists: true})
                    setHasSubmitted(true)
                }
            })
            .catch((error) => { 
                alert('Oops! Something went wrong. Please reload the page.')
                console.error(error)
            })
        }
        else {
            // No email found in URL, set with dummy data to prevent submission
            setCoachExists({isActive: true, exists: true})
            setHasSubmitted(true)
        }
    },[])

    // Function to handle form submission
    function onSubmit(data:FormStructure){
        setIsSubmitting(true)

        const formData = new FormData()
        formData.append("id", idFromURL!)
        formData.append("summary", data.summary)
        formData.append("firstLink", data.firstLink)
        formData.append("secondLink", data.secondLink)
        formData.append("certified", String(data.certified))
        formData.append("recordCheck", String(data.recordCheck))
        formData.append("disclaimer", String(data.disclaimer))

        if (data.certificationFile) {
            formData.append("certificationFile", data.certificationFile[0])
        }
        if (data.thirdLink) {
            formData.append("thirdLink", data.thirdLink)
        }

        axios.post(`${process.env.REACT_APP_SKILLJA_URL}/screening/create_coach_screening/`, formData, {
            headers: {
                "X-CSRFToken": csrfToken,
            },
            withCredentials: true,
        })
        .then((res) => {
            if (res.status === 200) {
                setIsSubmitting(false)
                setHasSubmitted(true)
            } 
        })
        .catch((error) => { 
            setIsSubmitting(false)
            alert('You have already submitted this form!')
            console.error(error)
        })
    }

    return (
        <div className="flex flex-col">
            <Header useCase="onboarding" />
            <div className="py-2 px-4 lg:px-12 mx-auto mt-5 mb-20">
                <section>
                    <h1 className="font-source text-4xl font-bold mx-auto text-center">
                        Coach Screening Form
                    </h1>
                    <h2 className="font-kulim text-lg mx-auto text-center mt-6">
                        Tell us more about your background so athletes can book with confidence.
                    </h2>
                </section>
                {isSubmitting ?
                    <div className="my-36">
                        <LoadingAnimation />
                    </div>
                :
                (
                    !hasSubmitted && !coachExists?.isActive && coachExists?.exists ?
                        <form className="flex flex-col w-full lg:max-w-2xl xl:max-w-3xl my-16 font-kulim" onSubmit={handleSubmit(onSubmit)}>
                            <div className="flex flex-col justify-start items-start mb-8">
                                <label htmlFor="summary">
                                    Give us a detailed summary about your coaching background (relevant work, education, and experiences that demonstrate your abilities).
                                    <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    id="summary"
                                    className="form-input mt-2.5 px-4 resize-y min-h-28 max-h-28"
                                    placeholder="Describe your past coaching background"
                                    autoComplete="off"
                                    {...register("summary", {
                                        required: "Coaching summary is required",
                                        minLength: {
                                            value: 300,
                                            message: "Summary must be at least 300 characters long"
                                        },
                                        maxLength: {
                                            value: 800,
                                            message: "Summary must be at most 800 characters long"
                                        }
                                    })}
                                />
                                {errors.summary && (
                                    <p className="text-red-500 text-sm my-3 mx-auto text-center">
                                        *{errors.summary.message}*
                                    </p>
                                )}
                            </div>
                            <div className="flex flex-col justify-start items-start mb-8">
                                <label>
                                    Please provide any links related to the experiences described above (blog posts, personal website/brand, social media profiles, company/club websites, etc.).
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id='firstLink'
                                    required={true}
                                    className="form-input mt-2.5 px-4"
                                    autoComplete="off"
                                    placeholder="LinkedIn, Instagram, Personal Website, etc."
                                    {...register("firstLink", {
                                        required: "Link is required",
                                        pattern: {
                                            value: /^https?:\/\/[^\s/$.?#].[^\s]*$/i,
                                            message: "Enter a valid, complete link (e.g., https://example.com)"
                                        },
                                    })}
                                />
                                <input
                                    type="text"
                                    id='secondLink'
                                    required={true}
                                    className="form-input my-2.5 px-4"
                                    autoComplete="off"
                                    placeholder="Work Experience (Club, Company Website, etc.)"
                                    {...register("secondLink", {
                                        required: "Link is required",
                                        pattern: {
                                            value: /^https?:\/\/[^\s/$.?#].[^\s]*$/i,
                                            message: "Enter a valid, complete link (e.g., https://example.com)"
                                        },
                                    })}
                                />
                                <input
                                    type="text"
                                    id='thirdLink'
                                    className="form-input px-4"
                                    autoComplete="off"
                                    placeholder="Additional Link (optional)"
                                    {...register("thirdLink", {
                                        pattern: {
                                            value: /^https?:\/\/[^\s/$.?#].[^\s]*$/i,
                                            message: "Enter a valid, complete link (e.g., https://example.com)"
                                        },
                                    })}
                                />
                                {errors.firstLink && (
                                    <p className="text-red-500 text-sm my-3 mx-auto text-center">
                                        *{errors.firstLink.message}*
                                    </p>
                                )}
                                {errors.secondLink && (
                                    <p className="text-red-500 text-sm my-3 mx-auto text-center">
                                        *{errors.secondLink.message}*
                                    </p>
                                )}
                                {errors.thirdLink && (
                                    <p className="text-red-500 text-sm my-3 mx-auto text-center">
                                        *{errors.thirdLink.message}*
                                    </p>
                                )}
                            </div>
                            <div className="flex flex-col justify-start items-start mb-8">
                                <label htmlFor="certified">
                                    Do you hold any relevant coaching certifications (NCCP or Sport-specific certifications)?
                                    <span className="text-red-500">*</span>
                                </label> 
                                <select
                                    className="form-input mt-2.5 px-4 max-w-96"
                                    id="certified"
                                    defaultValue=""
                                    {...register("certified", { required: true })}
                                >
                                    <option value="" disabled>Select Option</option>
                                    <option value="yes">Yes</option>
                                    <option value="in-progress">Currently Pursuing!</option>
                                    <option value="no">No</option>
                                </select>
                            </div>
                            <div className="flex flex-col justify-start items-start mb-8">
                                <label htmlFor="certificationFile">
                                    If 'Yes' was selected for the above question, please upload an image of your certification (PNG, JPG, or PDF accepted).
                                </label>
                                <input 
                                    type="file" 
                                    id="certificationFile" 
                                    accept="image/png, image/jpeg, image/jpg"
                                    className="mt-2.5 cursor-pointer"
                                    {...register("certificationFile")}
                                />    
                            </div>
                            <div className="flex flex-col justify-start items-start mb-8">
                                <label htmlFor="recordCheck">
                                    Would you be able to pass a criminal background check and vulnerable sector screening if requested?
                                    We ask this to ensure a safe environment for athletes and parents.
                                    <span className="text-red-500">*</span> <br/>
                                </label>
                                <select
                                    id="recordCheck"
                                    defaultValue=""
                                    className="form-input mt-2.5 px-4 max-w-96"
                                    required={true}
                                    {...register("recordCheck", { required: true })}
                                >
                                    <option value="" disabled>Select Option</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                </select>
                            </div>
                            <div className="flex mb-16">
                                <input
                                    type="checkbox"
                                    className="form-input w-8"
                                    required={true}
                                    {...register("disclaimer", { required: true })}
                                />
                                <p className="ml-6">
                                    By clicking this checkbox, you confirm that all answers provided are honest to the best of your knowledge. 
                                    Dishonest responses may result in indefinite removal from the SkillJa platform.
                                </p>
                                {errors.disclaimer && (
                                    <p className="text-red-500 text-sm mt-2">
                                        *You must confirm your answers are honest*
                                    </p>
                                )}
                            </div>
                            <button className="form-btn mx-auto" disabled={isSubmitting || hasSubmitted}>
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </button>
                        </form>
                    :
                        <section className="flex flex-col font-kulim mt-20 w-full lg:max-w-2xl text-center mx-auto">
                            <h3 className="font-semibold text-lg">
                                Thank you for your submission!
                            </h3>
                            <p className="mt-4 mb-12">
                                We're reviewing your responses and encourage you to begin building out your coaching profile. 
                                If we need more information or determine SkillJa isn't the right fit at this time, we'll follow up via email.
                            </p>
                            <a className="form-btn mx-auto px-8" href="/">
                                Home
                            </a>
                        </section>
                )
                }           
            </div>
            <Footer />
        </div>
    )
}